import { machineRegistry, WorkflowType } from '@rosta/workflow-machines';

/**
 * Look up the next state for a given workflow type, current state, and event
 * using the shared machine definitions from @rosta/workflow-machines.
 * Falls back to undefined if the workflow type is not registered.
 */
export function getMachineNextState(
  workflowType: string,
  currentState: string,
  event: string,
): string | undefined {
  const entry = machineRegistry[workflowType as WorkflowType];
  if (!entry) return undefined;

  const stateConfig = (entry.config as Record<string, unknown>)?.states as Record<string, { on?: Record<string, unknown> }> | undefined;
  const current = stateConfig?.[currentState];
  if (!current?.on) return undefined;

  const transition = current.on[event] as { target?: string } | { target?: string }[] | undefined;
  if (!transition) return undefined;

  // XState transition can be { target: string } or { target: string }[]
  if (Array.isArray(transition)) {
    return transition[0]?.target;
  }
  return transition.target;
}

/**
 * Get all allowed events for a given workflow type and current state.
 */
export function getAllowedEvents(
  workflowType: string,
  currentState: string,
): string[] {
  const entry = machineRegistry[workflowType as WorkflowType];
  if (!entry) return [];

  const stateConfig = (entry.config as Record<string, unknown>)?.states as Record<string, { on?: Record<string, unknown> }> | undefined;
  const current = stateConfig?.[currentState];
  if (!current?.on) return [];

  return Object.keys(current.on);
}

/**
 * Build a chain of transitions from a list of events.
 * Returns the final state and the transitions array.
 */
export function buildTransitionChain(
  workflowType: string,
  initialState: string,
  events: string[],
): { finalState: string; transitions: Array<{
  sequence: number;
  event: string;
  fromState: string;
  toState: string;
  timestamp: string;
  triggeredBy: string;
}> } {
  let currentState = initialState;
  const transitions: Array<{
    sequence: number;
    event: string;
    fromState: string;
    toState: string;
    timestamp: string;
    triggeredBy: string;
  }> = [];

  for (const event of events) {
    const nextState = getMachineNextState(workflowType, currentState, event);
    if (!nextState) break;

    transitions.push({
      sequence: transitions.length + 1,
      event,
      fromState: currentState,
      toState: nextState,
      timestamp: new Date().toISOString(),
      triggeredBy: 'current-user',
    });
    currentState = nextState;
  }

  return { finalState: currentState, transitions };
}
